import { InjectionToken } from '@angular/core';
import { Params } from '@angular/router';
import { isWorkplaceUrl, WORKPLACE_PATH } from '../../service/workplace.config';
import { toNewOrderQuery } from './state/order-new.url';

/**
 * Every address the new editor is entered by and left through (design §5.2), in ONE place — each increment moves a target by editing a line
 * here, never by hunting through the pages. Pure: a builder returns what a navigation needs (`commands`, `queryParams`, `state`), the caller
 * hands it to the router.
 *
 *  - `/order/new` — step 1 (the choice, no cart yet); `/order/new/:cartId` — step 2 (2b); `/order/:id/edit` — the change of an order (3a);
 *  - every way in by a link opens a NEW conversation: `newOrderLink` makes a new draft id (`?d=`), so what the previous client's conversation
 *    left in the form never gets into the new one;
 *  - the page the operator came from travels in the navigation state (`backLink`) — accepted only when it is an address of the «Заказы» /
 *    «Архив» pages or of the workplace (`/call-center`, stage 4), anything else is the list.
 */

export const NEW_ORDER_PATH = '/order/new';
export const ORDERS_PATH = '/orders';

/** `NavigationExtras.state` key of the page the operator came from (the key `OldEditorReturnService` and the cards use, too). */
export const BACK_LINK_STATE_KEY = 'backLink';
/**
 * `NavigationExtras.state` key of the number «Новый заказ на этот номер» opens the page with (digits). The number of a client does not travel in the ADDRESS: an address goes into
 * the `Referer` of the first request, into the journal of the server and into the breadcrumbs of the error tracker. The page reads it once and its own rewrite of the entry of the
 * history (`replaceUrl`, with the way back only) takes it away.
 */
export const ENTRY_PHONE_STATE_KEY = 'orderEditorPhone';
/** `NavigationExtras.state` key of the editor's own moves inside one conversation — its value is the conversation's draft id: such a move brings the form back silently. */
export const CONVERSATION_STATE_KEY = 'orderEditorConversation';

/**
 * `NavigationExtras.state` key of «Привязать звонок» as the booking was made (stage 4, D2): `true` — the booking carries the call (E11 with `callId`). Step 2 reads it to say, when the order is made, whether the order is the call's.
 * A boolean in the history state — no number, no name; absent (a booking continued from elsewhere) is taken as «not known», i.e. not linked.
 */
export const CALL_LINKED_STATE_KEY = 'orderEditorCallLinked';
/** `NavigationExtras.state` key of the order that was just made, for the workplace it leads back to (stage 4, D2): see {@link OrderCreatedNote}. Read once by the workplace page; nothing of it is in the address. */
export const ORDER_CREATED_STATE_KEY = 'orderCreated';

/** «Заказ {номер} создан [и привязан к звонку]» on the workplace after «Создать заказ»: the order, its number, whether the booking carried the call. No client, no phone. */
export interface OrderCreatedNote {
  orderId: number;
  /** The call the workplace held when the order was made (B2′: another call may be held by the time the workplace is entered — the line is then none of its business). Absent — no call was held. */
  callId?: string;
  /** The number the operator knows the order by (`uniqueNumber`); the order id when the server gave none. */
  number: string;
  linked: boolean;
}

export interface EditorNavigation {
  commands: Array<string | number>;
  queryParams: Params;
  state: Record<string, unknown>;
}

/**
 * The pages the operator may come from: «Заказы» / «Архив», a card or the old editor (`/order/…`), and the workplace (`/call-center`, with a query or a fragment at most — `/call-center-old`
 * and `/call-center/x` are not it).
 */
const BACK_LINK_RE = /^\/(?:orders(?:-archive)?(?:[/?#][^\\\s]*)?|order\/[^\\\s]*|call-center(?:[?#][^\\\s]*)?)$/;

/** The address of the page the operator came from, if it is one of ours; the list otherwise (never a foreign address, never a script). */
export function safeBackLink(value: unknown): string {
  return typeof value === 'string' && BACK_LINK_RE.test(value) ? value : ORDERS_PATH;
}

/** The operator came from the workplace (`/call-center`, stage 4): the editor then leads back to it and its crumb says so. */
export function fromWorkplace(backLink: string | undefined): boolean {
  return isWorkplaceUrl(backLink);
}

/** The key of the first crumb of the three pages: «Рабочее место» when the operator came from the workplace, «Заказы» otherwise. A field of the page — the template holds no call. */
export function crumbKeyOf(backLink: string | undefined): 'orderEditor.page.crumbWorkplace' | 'orderEditor.page.crumbOrders' {
  return fromWorkplace(backLink) ? 'orderEditor.page.crumbWorkplace' : 'orderEditor.page.crumbOrders';
}

/** The key of the button that leads back («На рабочее место» / «К заказам») — the same rule as the crumb. */
export function backKeyOf(backLink: string | undefined): 'orderEditor.page.toWorkplace' | 'orderEditor.page.toOrders' {
  return fromWorkplace(backLink) ? 'orderEditor.page.toWorkplace' : 'orderEditor.page.toOrders';
}

const DRAFT_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** Six random characters — not personal data, not a secret, only the name of a conversation. */
export function newDraftId(random: () => number = Math.random): string {
  let id = '';
  for (let i = 0; i < 6; i++) {
    id += DRAFT_ALPHABET.charAt(Math.floor(random() * DRAFT_ALPHABET.length) % DRAFT_ALPHABET.length);
  }
  return id;
}

export interface NewOrderLinkOptions {
  /** «Новый заказ на этот номер»: the number the page opens with — read once and taken out of the address. */
  phone?: string;
  clientId?: number;
  /** The page the operator leaves from. */
  backLink?: string;
  /** An existing conversation (a move inside it): the form is brought back silently. Absent — a new conversation. */
  draftId?: string;
  brandId?: number;
  cityId?: number;
  date?: string;
  defaultBrandId?: number;
  /** The booking that is held while the choice is changed (`held_cart_id`). */
  heldCartId?: number;
}

/**
 * Step 1. Without `draftId` a NEW conversation starts: a clean form with the given number, if any. The number goes in the navigation state, never in the address
 * (`ENTRY_PHONE_STATE_KEY`); a link written by hand with `?phone=` still opens the page (it is read once and the address is rewritten before the first request).
 */
export function newOrderLink(options: NewOrderLinkOptions = {}): EditorNavigation {
  const draftId = options.draftId ?? newDraftId();
  const queryParams: Params = toNewOrderQuery(
    { brandId: options.brandId, cityId: options.cityId, date: options.date, draftId, ...(options.heldCartId !== undefined ? { heldCartId: options.heldCartId } : {}) },
    { defaultBrandId: options.defaultBrandId },
  );
  const phone = (options.phone ?? '').replace(/\D/g, '').slice(0, 15);
  if (options.clientId !== undefined) {
    queryParams['client_id'] = String(options.clientId);
  }
  return {
    commands: [NEW_ORDER_PATH],
    queryParams,
    state: {
      [BACK_LINK_STATE_KEY]: safeBackLink(options.backLink),
      ...(phone ? { [ENTRY_PHONE_STATE_KEY]: phone } : {}),
      ...(options.draftId ? { [CONVERSATION_STATE_KEY]: options.draftId } : {}),
    },
  };
}

/** The number a navigation state carries for the page (digits, at most 15), if it carries one. */
export function entryPhoneOf(state: unknown): string | undefined {
  const value = state !== null && typeof state === 'object' ? (state as Record<string, unknown>)[ENTRY_PHONE_STATE_KEY] : undefined;
  const digits = typeof value === 'string' ? value.replace(/\D/g, '').slice(0, 15) : '';
  return digits || undefined;
}

/**
 * Step 2 (2b): the booking `cartId`; the conversation's draft id travels so that «Назад» of step 2 finds its form. `callLinked` (stage 4, D2): the booking was made with the call — step 2 tells the workplace so when the order is made.
 */
export function cartLink(cartId: number, options: { backLink?: string; draftId?: string; callLinked?: boolean } = {}): EditorNavigation {
  return {
    commands: [NEW_ORDER_PATH, cartId],
    queryParams: {},
    state: {
      [BACK_LINK_STATE_KEY]: safeBackLink(options.backLink),
      ...(options.draftId ? { [CONVERSATION_STATE_KEY]: options.draftId } : {}),
      ...(options.callLinked !== undefined ? { [CALL_LINKED_STATE_KEY]: options.callLinked } : {}),
    },
  };
}

/** «Привязать звонок» as a navigation state carries it: `true` only when the booking was made with the call. */
export function callLinkedOf(state: unknown): boolean {
  return state !== null && typeof state === 'object' && (state as Record<string, unknown>)[CALL_LINKED_STATE_KEY] === true;
}

/** The change of an order in work (3a). */
export function orderEditLink(orderId: number, options: { backLink?: string } = {}): EditorNavigation {
  return { commands: ['/order', orderId, 'edit'], queryParams: {}, state: { [BACK_LINK_STATE_KEY]: safeBackLink(options.backLink) } };
}

/**
 * «Снять бронь» (and «Бронь снята → Закрыть»): back to step 1 of the SAME conversation — what was filled stays, and the form comes back
 * without a question.
 */
export function backToChoiceLink(draftId: string, backLink?: string, scope?: { brandId: number; cityId: number; date: string }): EditorNavigation {
  // the brand, the city and the day are the booking's: step 1 reads them from its address, the form holds none of them
  return newOrderLink({ draftId, ...(backLink !== undefined ? { backLink } : {}), ...(scope ?? {}) });
}

/**
 * «Изменить» of «Когда и где» on step 2: step 1 of the SAME conversation with the booking still held — its pieces and its slot are not counted as taken (`held_cart_id`), its brand,
 * city and day are in the address. The booking is not taken off: what the operator changes there is saved into it (E14, E15) or made again in its place (E11 with `replaceCartId`).
 */
export function changeBookingLink(cartId: number, draftId: string, scope: { brandId: number; cityId: number; date: string }, backLink?: string): EditorNavigation {
  return newOrderLink({ draftId, heldCartId: cartId, ...(backLink !== undefined ? { backLink } : {}), ...scope });
}

/** The query of the list `backLink` leads to, when it is the list itself (`/orders?view=late…`) — so that the operator comes back to the same view. */
export function listParamsOf(backLink: string | undefined): Params {
  return listQuery(safeBackLink(backLink));
}

function listQuery(backLink: string): Params {
  const [path, query = ''] = backLink.split('#')[0].split('?');
  if (path !== ORDERS_PATH) {
    return {};
  }
  const params: Params = {};
  new URLSearchParams(query).forEach((value, key) => {
    if (key !== 'order_id') {
      params[key] = value;
    }
  });
  return params;
}

/**
 * After an order is made from a booking (or found already made): the bare list and the order's own live drawer open. The filters of the list the operator came from do NOT go along — a booking was
 * found in «Корзины», the order that became of it is not there, and the drawer would stand over a list that has no such row.
 */
export function ordersLink(orderId: number): EditorNavigation {
  return { commands: [ORDERS_PATH], queryParams: { order_id: orderId }, state: {} };
}

/**
 * Where «Создать заказ» / «Сохранить» / the transfer lead: back to the workplace with the order marked (`?order_id=`, the workplace opens its own shutter) when the operator came from it, the list with the
 * order's drawer open (`ordersLink`) otherwise — as it has always been. `created` (only «Создать заказ» gives it, stage 4 D2): the workplace says «Заказ … создан [и привязан к звонку]» — it travels in the navigation state,
 * and only to the workplace (the list has no such line).
 */
export function returnLink(orderId: number, backLink: string | undefined, created?: Omit<OrderCreatedNote, 'orderId'>): EditorNavigation {
  return fromWorkplace(backLink)
    ? {
      commands: [WORKPLACE_PATH],
      queryParams: { order_id: orderId },
      state: created ? { [ORDER_CREATED_STATE_KEY]: { orderId, number: created.number, linked: created.linked, ...(created.callId ? { callId: created.callId } : {}) } satisfies OrderCreatedNote } : {},
    }
    : ordersLink(orderId);
}

/** The order that was just made, from the navigation state the workplace was entered with — checked, because a history state is not to be trusted blindly (a hand-made one, an old one). */
export function orderCreatedOf(state: unknown): OrderCreatedNote | undefined {
  const raw = state !== null && typeof state === 'object' ? (state as Record<string, unknown>)[ORDER_CREATED_STATE_KEY] : undefined;
  if (raw === null || typeof raw !== 'object') {
    return undefined;
  }
  const note = raw as Record<string, unknown>;
  const { orderId, number, linked, callId } = note;
  const callIdOk = callId === undefined || (typeof callId === 'string' && /^[^\s<>"'\\]{1,64}$/.test(callId));
  return typeof orderId === 'number' && Number.isInteger(orderId) && orderId > 0
    && typeof number === 'string' && /^[\w.\-/ ]{1,40}$/.test(number)
    && typeof linked === 'boolean' && callIdOk
    ? { orderId, number, linked, ...(typeof callId === 'string' ? { callId } : {}) }
    : undefined;
}

/** Where «Забронировать» and «Продолжить заказ» of a booking lead: the old editor until step 2 was built (2a-1, 2a-2), the new step 2 from 2b — the one line to change. */
export type BookedTarget =
  | { kind: 'old-editor'; orderId: number }
  | { kind: 'route'; link: EditorNavigation };

/** Step 2 is built (2b): the booking opens in `/order/new/:cartId`. `false` would send it to the old editor again. */
export const NEW_STEP_TWO_READY = true;

export function afterBookTarget(cartId: number, options: { backLink?: string; draftId?: string; callLinked?: boolean } = {}): BookedTarget {
  return NEW_STEP_TWO_READY
    ? { kind: 'route', link: cartLink(cartId, options) }
    : { kind: 'old-editor', orderId: cartId };
}

/** Where «Изменить» of an order in work leads: the old editor until the change was built (3a), the new page from 3a — the one line to change. */
export type EditTarget =
  | { kind: 'old-editor'; orderId: number }
  | { kind: 'route'; link: EditorNavigation };

/** The change of an order is built (3a): «Изменить» opens `/order/:id/edit`. `false` would send it to the old editor again. */
export const NEW_EDIT_READY = true;

export function afterEditTarget(orderId: number, options: { backLink?: string } = {}): EditTarget {
  return NEW_EDIT_READY
    ? { kind: 'route', link: orderEditLink(orderId, options) }
    : { kind: 'old-editor', orderId };
}

/**
 * 3b — the transfer of an order to another day or another store («Перенести на другой день или в другой магазин» on the page of the change): the block, the choice of the day and the store, the dialogs
 * of the preview and of the transfer. Everything new of 3b hides behind this ONE line until the stand switches it: `false` — the page of the change is that of 3a, with no word of the transfer.
 */
export const NEW_TRANSFER_READY = true;

/**
 * What the page of the change asks to know whether the transfer is built: the line above by default. A spec provides its own value (`{ provide: TRANSFER_READY, useValue: true }`) and runs the whole transfer whatever the line
 * says, so that the switch of the stand never changes what the specs prove.
 */
export const TRANSFER_READY = new InjectionToken<boolean>('cc.orderEditor.transferReady', { providedIn: 'root', factory: () => NEW_TRANSFER_READY });

/**
 * The way out of «Новый заказ» to the OLD page, kept for the time of the launch: if the new page does not cover a case or fails, the operator goes on with the same order on the old page (the link
 * «Оформить на старой странице» in the head of both steps, the same action next to a technical refusal). `false` takes all of it away — the link, the buttons, the windows, the action of the dialog «Бронь не создана».
 * It is for the creation of an order only: the change of an order (`/order/:id/edit`) has no such way out and no switch.
 */
export const OLD_EDITOR_ESCAPE_READY = true;

/** What the two pages ask to know whether the way out is on: the line above by default. A spec provides its own value (`{ provide: OLD_EDITOR_ESCAPE, useValue: true }`). */
export const OLD_EDITOR_ESCAPE = new InjectionToken<boolean>('cc.orderEditor.oldEditorEscape', { providedIn: 'root', factory: () => OLD_EDITOR_ESCAPE_READY });
